import React from 'react';
import {
  Receipt, AlertCircle, CheckCircle, ClipboardList,
  Search, Download, Plus, Save, Loader2, X, Edit2, Trash2, Upload, Eye,
  DollarSign, TrendingUp, TrendingDown, ArrowUpRight, RefreshCw
} from 'lucide-react';
import AccountCodePicker from '../../../components/admin/AccountCodePicker';
import FundPicker from '../../../components/admin/FundPicker';
import CampaignPicker from '../../../components/admin/CampaignPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXPENSE_CATEGORIES = [
  "Travel", "Hotel", "Meals", "Supplies", "Events",
  "Food Assistance", "Hygiene Items", "Admin / Operations", "Other"
];

const PAYMENT_METHODS = [
  "Cash", "Credit Card", "Debit Card", "Bank Transfer",
  "Check", "Zelle", "Venmo", "PayPal", "Other"
];

const REIMB_STATUSES = ["owed", "pending", "approved", "paid"];

const StatusBadge = ({ status }) => {
  const colors = {
    owed: 'bg-red-100 text-red-700',
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700',
    not_reimbursed: 'bg-gray-100 text-gray-600',
    reimbursed: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`} data-testid={`status-badge-${status}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export { StatusBadge, EXPENSE_CATEGORIES, PAYMENT_METHODS, REIMB_STATUSES };

export const FinanceDashboardTab = ({ dashData, sortedCategoryTotals, dateFilter, setDateFilter, onFilterApply }) => {
  const netPositive = (dashData.net_balance || 0) >= 0;

  return (
  <div className="space-y-6" data-testid="finance-dashboard">
    {/* Date Filter */}
    <div className="bg-white p-4 border border-[#E5E7EB] rounded-lg flex flex-wrap items-end gap-3" data-testid="finance-date-filter">
      <div>
        <label className="block text-xs font-medium text-[#4B5563] mb-1">From</label>
        <input type="date" value={dateFilter.from} onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
          className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none" data-testid="finance-date-from" />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#4B5563] mb-1">To</label>
        <input type="date" value={dateFilter.to} onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
          className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none" data-testid="finance-date-to" />
      </div>
      <button onClick={onFilterApply} className="btn-primary text-xs px-4 py-2" data-testid="finance-filter-apply">Apply</button>
      <button onClick={() => { setDateFilter({ from: '', to: '' }); onFilterApply(); }}
        className="btn-secondary text-xs px-4 py-2" data-testid="finance-filter-reset">All Time</button>
      {(dateFilter.from || dateFilter.to) && (
        <span className="text-xs text-[#4B5563] ml-2">
          Showing: {dateFilter.from || 'start'} to {dateFilter.to || 'now'}
        </span>
      )}
    </div>

    {/* Financial Summary - Revenue / Expenses / Net */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="financial-summary">
      <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#4B5563] font-medium">Donation Revenue</span>
          <DollarSign className="w-4 h-4 text-green-600" />
        </div>
        <p className="text-2xl font-bold text-green-700" data-testid="donation-revenue">
          ${(dashData.donation_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
        </p>
        <div className="flex gap-3 mt-1">
          <span className="text-xs text-[#9CA3AF]">{dashData.donation_count || 0} donations</span>
          {(dashData.recurring_donations || 0) > 0 && (
            <span className="text-xs text-[#BA1826] flex items-center gap-0.5">
              <RefreshCw className="w-3 h-3" /> {dashData.recurring_donations} recurring
            </span>
          )}
        </div>
      </div>
      <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#4B5563] font-medium">Total Expenses</span>
          <Receipt className="w-4 h-4 text-[#BA1826]" />
        </div>
        <p className="text-2xl font-bold text-[#0A0A0A]" data-testid="total-expenses">
          ${(dashData.total_expenses || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
        </p>
        <span className="text-xs text-[#9CA3AF]">{dashData.expense_count || 0} records</span>
      </div>
      <div className={`bg-white p-5 border-2 rounded-lg ${netPositive ? 'border-green-200' : 'border-red-200'}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#4B5563] font-medium">Net Balance</span>
          {netPositive ? <TrendingUp className="w-4 h-4 text-green-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
        </div>
        <p className={`text-2xl font-bold ${netPositive ? 'text-green-700' : 'text-red-700'}`} data-testid="net-balance">
          {netPositive ? '+' : ''}${(dashData.net_balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
        </p>
        <span className="text-xs text-[#9CA3AF]">Revenue minus expenses</span>
      </div>
    </div>

    {/* Reimbursements + Trips + Team row */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#4B5563]">Reimb. Owed</span>
          <AlertCircle className="w-4 h-4 text-red-500" />
        </div>
        <p className="text-xl font-bold text-red-600" data-testid="reimb-owed">
          ${(dashData.total_reimbursements_owed || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
        </p>
        <p className="text-xs text-[#9CA3AF] mt-1">Owed + Pending</p>
      </div>
      <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#4B5563]">Reimb. Paid</span>
          <CheckCircle className="w-4 h-4 text-green-500" />
        </div>
        <p className="text-xl font-bold text-green-600" data-testid="reimb-paid">
          ${(dashData.total_reimbursements_paid || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
        </p>
        <p className="text-xs text-[#9CA3AF] mt-1">{dashData.reimbursement_count || 0} total</p>
      </div>
      <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#4B5563]">Trips</span>
          <ArrowUpRight className="w-4 h-4 text-blue-500" />
        </div>
        <p className="text-xl font-bold text-[#0A0A0A]" data-testid="trip-count">{dashData.trip_count || 0}</p>
        <p className="text-xs text-[#9CA3AF] mt-1">Logged trips</p>
      </div>
      <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#4B5563]">Pending Reviews</span>
          <ClipboardList className="w-4 h-4 text-yellow-500" />
        </div>
        <p className="text-xl font-bold text-[#0A0A0A]" data-testid="pending-reviews">{dashData.pending_submissions || 0}</p>
        <p className="text-xs text-[#9CA3AF] mt-1">{dashData.team_member_count || 0} team</p>
      </div>
    </div>

    {/* Monthly Revenue Trend */}
    {(dashData.monthly_revenue || []).length > 0 && (
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg" data-testid="monthly-revenue-chart">
        <h3 className="font-heading font-semibold mb-4 text-sm">Monthly Donation Revenue</h3>
        <div className="space-y-3">
          {[...(dashData.monthly_revenue || [])].reverse().map((m) => {
            const maxRev = Math.max(...(dashData.monthly_revenue || []).map(r => r.revenue));
            const pct = maxRev > 0 ? (m.revenue / maxRev) * 100 : 0;
            return (
              <div key={m.month}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#4B5563]">{m.month}</span>
                  <span className="font-medium">${m.revenue.toLocaleString(undefined, {minimumFractionDigits: 2})} ({m.count})</span>
                </div>
                <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}

    {/* Expense Category Breakdown */}
    {Object.keys(dashData.category_totals || {}).length > 0 && (
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg">
        <h3 className="font-heading font-semibold mb-4 text-sm">Expenses by Category</h3>
        <div className="space-y-3">
          {sortedCategoryTotals.map(([cat, amount]) => {
            const pct = dashData.total_expenses > 0 ? (amount / dashData.total_expenses) * 100 : 0;
            return (
              <div key={cat}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#4B5563]">{cat}</span>
                  <span className="font-medium">${amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div className="h-full bg-[#BA1826] rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}

    {/* Recent Trips + Recent Receipts */}
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg">
        <h3 className="font-heading font-semibold mb-4 text-sm">Recent Trips</h3>
        {(dashData.recent_trips || []).length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">No trips logged yet</p>
        ) : (
          <div className="space-y-3">
            {dashData.recent_trips.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b border-[#F1F5F9] last:border-0">
                <div>
                  <p className="text-sm font-medium">{t.destination}</p>
                  <p className="text-xs text-[#4B5563]">{t.purpose}</p>
                </div>
                <span className="text-xs text-[#9CA3AF]">{t.date}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg">
        <h3 className="font-heading font-semibold mb-4 text-sm">Recent Receipts</h3>
        {(dashData.recent_receipts || []).length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">No receipts uploaded yet</p>
        ) : (
          <div className="space-y-3">
            {dashData.recent_receipts.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2 border-b border-[#F1F5F9] last:border-0">
                <div>
                  <p className="text-sm font-medium">{e.description}</p>
                  <p className="text-xs text-[#4B5563]">${e.amount} - {e.category}</p>
                </div>
                {e.receipt_file && (
                  <a href={`${BACKEND_URL}/api/uploads/${e.receipt_file}`} target="_blank" rel="noreferrer" className="text-[#BA1826] hover:underline text-xs">View</a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

export const FinanceExpensesTab = ({ ctx }) => {
  const {
    expenses, expenseFilters, setExpenseFilters, showExpenseForm, setShowExpenseForm,
    expenseForm, setExpenseForm, editingExpense, resetExpenseForm, handleSaveExpense,
    handleEditExpense, handleDeleteExpense, handleReceiptUpload, uploadingReceipt,
    handleExport, trips, saving, inputClass, labelClass
  } = ctx;

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <div className="flex flex-wrap gap-2 flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input type="text" placeholder="Search expenses..." value={expenseFilters.search}
              onChange={(e) => setExpenseFilters(f => ({ ...f, search: e.target.value }))}
              className="pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-md text-sm w-48" data-testid="expense-search" />
          </div>
          <select value={expenseFilters.category} onChange={(e) => setExpenseFilters(f => ({ ...f, category: e.target.value }))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" data-testid="expense-filter-category">
            <option value="">All Categories</option>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={expenseFilters.reimbursed} onChange={(e) => setExpenseFilters(f => ({ ...f, reimbursed: e.target.value }))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" data-testid="expense-filter-reimbursed">
            <option value="">All Statuses</option>
            <option value="not_reimbursed">Not Reimbursed</option>
            <option value="pending">Pending</option>
            <option value="reimbursed">Reimbursed</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleExport('expenses')} className="flex items-center gap-1 px-3 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]" data-testid="export-expenses-btn">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => { resetExpenseForm(); setShowExpenseForm(true); }} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420]" data-testid="add-expense-btn">
            <Plus className="w-4 h-4" /> Add Expense
          </button>
        </div>
      </div>

      {showExpenseForm && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="expense-form">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-heading font-semibold text-sm">{editingExpense ? 'Edit Expense' : 'New Expense'}</h3>
            <button onClick={resetExpenseForm} className="text-[#4B5563] hover:text-[#0A0A0A]"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={labelClass}>Date *</label><input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm(f => ({ ...f, date: e.target.value }))} className={inputClass} data-testid="expense-date" /></div>
            <div><label className={labelClass}>Paid By *</label><input type="text" value={expenseForm.paid_by} onChange={(e) => setExpenseForm(f => ({ ...f, paid_by: e.target.value }))} className={inputClass} placeholder="Who paid?" data-testid="expense-paid-by" /></div>
            <div><label className={labelClass}>Category *</label>
              <select value={expenseForm.category} onChange={(e) => setExpenseForm(f => ({ ...f, category: e.target.value }))} className={inputClass} data-testid="expense-category">
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>
                Account Code <span className="text-[#BA1826]">*</span>
                <span className="ml-1 text-[10px] text-[#9CA3AF] font-normal">(4-digit GAAP code)</span>
              </label>
              <AccountCodePicker
                value={expenseForm.account_code || ''}
                onChange={(code) => setExpenseForm(f => ({ ...f, account_code: code }))}
                typeFilter="expense"
                ariaLabel="Account code for this expense"
              />
              {!expenseForm.account_code && (
                <p className="mt-1 text-[10px] text-[#BA1826]">Required — pick the 4-digit code this expense maps to.</p>
              )}
            </div>
            <div><label className={labelClass}>Amount ($) *</label><input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} className={inputClass} data-testid="expense-amount" /></div>
            <div><label className={labelClass}>Payment Method</label>
              <select value={expenseForm.payment_method} onChange={(e) => setExpenseForm(f => ({ ...f, payment_method: e.target.value }))} className={inputClass} data-testid="expense-payment-method">
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><label className={labelClass}>Reimbursement Status</label>
              <select value={expenseForm.reimbursed} onChange={(e) => setExpenseForm(f => ({ ...f, reimbursed: e.target.value }))} className={inputClass} data-testid="expense-reimbursed">
                <option value="not_reimbursed">Not Reimbursed</option>
                <option value="pending">Pending</option>
                <option value="reimbursed">Reimbursed</option>
              </select>
            </div>
            <div><label className={labelClass}>Funding Source</label><input type="text" value={expenseForm.funding_source} onChange={(e) => setExpenseForm(f => ({ ...f, funding_source: e.target.value }))} className={inputClass} placeholder="e.g., General Fund, Grant #123" data-testid="expense-funding-source" /></div>
            <div><label className={labelClass}>Link to Trip</label>
              <select value={expenseForm.trip_id} onChange={(e) => setExpenseForm(f => ({ ...f, trip_id: e.target.value }))} className={inputClass} data-testid="expense-trip-id">
                <option value="">No trip linked</option>
                {trips.map(t => <option key={t.id} value={t.id}>{t.date} - {t.destination}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Fund (restriction)</label>
              <FundPicker value={expenseForm.fund_id || ''}
                onChange={(v) => setExpenseForm(f => ({ ...f, fund_id: v || '' }))} />
            </div>
            <div>
              <label className={labelClass}>Campaign / Program</label>
              <CampaignPicker value={expenseForm.campaign_id || ''}
                onChange={(v) => setExpenseForm(f => ({ ...f, campaign_id: v || '' }))} />
            </div>
            <div className="md:col-span-3"><label className={labelClass}>Description *</label><input type="text" value={expenseForm.description} onChange={(e) => setExpenseForm(f => ({ ...f, description: e.target.value }))} className={inputClass} placeholder="What was this expense for?" data-testid="expense-description" /></div>
            <div className="md:col-span-3"><label className={labelClass}>Notes</label><textarea value={expenseForm.notes} onChange={(e) => setExpenseForm(f => ({ ...f, notes: e.target.value }))} className={inputClass} rows={2} placeholder="Additional notes..." data-testid="expense-notes" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSaveExpense} disabled={saving} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420] disabled:opacity-50" data-testid="save-expense-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingExpense ? 'Update' : 'Save'}
            </button>
            <button onClick={resetExpenseForm} className="px-4 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="expenses-table">
            <thead className="bg-[#F8F9FA]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Paid By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Description</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#4B5563]">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-[#4B5563]">Receipt</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-[#4B5563]">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#4B5563]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-[#9CA3AF]">No expenses found. Click "Add Expense" to get started.</td></tr>
              ) : expenses.map((exp) => (
                <tr key={exp.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFAFA]">
                  <td className="px-4 py-3 text-sm">{exp.date}</td>
                  <td className="px-4 py-3 text-sm font-medium">{exp.paid_by}</td>
                  <td className="px-4 py-3 text-sm"><span className="px-2 py-0.5 bg-[#F1F5F9] rounded text-xs">{exp.category}</span></td>
                  <td className="px-4 py-3 text-sm max-w-xs truncate">{exp.description}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium">${exp.amount.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">
                    {exp.receipt_attached ? (
                      <a href={`${BACKEND_URL}/api/uploads/${exp.receipt_file}`} target="_blank" rel="noreferrer" className="text-green-600 hover:underline text-xs flex items-center justify-center gap-1" data-testid={`view-receipt-${exp.id}`}>
                        <Eye className="w-3 h-3" /> View
                      </a>
                    ) : (
                      <label className="cursor-pointer text-[#BA1826] hover:underline text-xs flex items-center justify-center gap-1" data-testid={`upload-receipt-${exp.id}`}>
                        {uploadingReceipt === exp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        Upload
                        <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.pdf,.webp"
                          onChange={(e) => { if (e.target.files[0]) handleReceiptUpload(exp.id, e.target.files[0]); }} />
                      </label>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={exp.reimbursed} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEditExpense(exp)} className="p-1 text-[#4B5563] hover:text-[#BA1826]" data-testid={`edit-expense-${exp.id}`}><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteExpense(exp.id)} className="p-1 text-[#4B5563] hover:text-red-600" data-testid={`delete-expense-${exp.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const FinanceReimbursementsTab = ({ ctx }) => {
  const {
    reimbursements, reimbFilters, setReimbFilters, showReimbForm, setShowReimbForm,
    reimbForm, setReimbForm, editingReimb, resetReimbForm, handleSaveReimb,
    handleEditReimb, handleDeleteReimb, handleExport, trips, expenses,
    saving, inputClass, labelClass
  } = ctx;

  return (
    <div className="space-y-4" data-testid="reimbursements-tab">
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input type="text" placeholder="Search by name..." value={reimbFilters.search}
              onChange={(e) => setReimbFilters(f => ({ ...f, search: e.target.value }))}
              className="pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-md text-sm w-48" data-testid="reimb-search" />
          </div>
          <select value={reimbFilters.status} onChange={(e) => setReimbFilters(f => ({ ...f, status: e.target.value }))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm" data-testid="reimb-filter-status">
            <option value="">All Statuses</option>
            {REIMB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleExport('reimbursements')} className="flex items-center gap-1 px-3 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]" data-testid="export-reimb-btn">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => { resetReimbForm(); setShowReimbForm(true); }} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420]" data-testid="add-reimb-btn">
            <Plus className="w-4 h-4" /> Add Reimbursement
          </button>
        </div>
      </div>

      {showReimbForm && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="reimb-form">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-heading font-semibold text-sm">{editingReimb ? 'Edit Reimbursement' : 'New Reimbursement'}</h3>
            <button onClick={resetReimbForm}><X className="w-5 h-5 text-[#4B5563]" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={labelClass}>Person Name *</label><input type="text" value={reimbForm.person_name} onChange={(e) => setReimbForm(f => ({ ...f, person_name: e.target.value }))} className={inputClass} placeholder="Who is owed?" data-testid="reimb-person" /></div>
            <div><label className={labelClass}>Total Amount ($) *</label><input type="number" step="0.01" value={reimbForm.total_amount} onChange={(e) => setReimbForm(f => ({ ...f, total_amount: parseFloat(e.target.value) || 0 }))} className={inputClass} data-testid="reimb-amount" /></div>
            <div><label className={labelClass}>Status *</label>
              <select value={reimbForm.status} onChange={(e) => setReimbForm(f => ({ ...f, status: e.target.value }))} className={inputClass} data-testid="reimb-status">
                {REIMB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className={labelClass}>Date Paid</label><input type="date" value={reimbForm.date_paid} onChange={(e) => setReimbForm(f => ({ ...f, date_paid: e.target.value }))} className={inputClass} data-testid="reimb-date-paid" /></div>
            <div><label className={labelClass}>Related Trip</label>
              <select value={reimbForm.related_trip_id} onChange={(e) => setReimbForm(f => ({ ...f, related_trip_id: e.target.value }))} className={inputClass} data-testid="reimb-trip">
                <option value="">None</option>
                {trips.map(t => <option key={t.id} value={t.id}>{t.date} - {t.destination}</option>)}
              </select>
            </div>
            <div><label className={labelClass}>Related Expenses</label>
              <select multiple value={reimbForm.related_expense_ids} onChange={(e) => { const selected = Array.from(e.target.selectedOptions, o => o.value); setReimbForm(f => ({ ...f, related_expense_ids: selected })); }}
                className={`${inputClass} min-h-[60px]`} data-testid="reimb-expenses">
                {expenses.map(ex => <option key={ex.id} value={ex.id}>{ex.date} - {ex.description} (${ex.amount})</option>)}
              </select>
              <p className="text-xs text-[#9CA3AF] mt-1">Hold Ctrl/Cmd to select multiple</p>
            </div>
            <div className="md:col-span-3"><label className={labelClass}>Payment Notes</label><textarea value={reimbForm.payment_notes} onChange={(e) => setReimbForm(f => ({ ...f, payment_notes: e.target.value }))} className={inputClass} rows={2} placeholder="How was this reimbursed? Check #, Zelle, etc." data-testid="reimb-notes" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSaveReimb} disabled={saving} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420] disabled:opacity-50" data-testid="save-reimb-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingReimb ? 'Update' : 'Save'}
            </button>
            <button onClick={resetReimbForm} className="px-4 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="reimbursements-table">
            <thead className="bg-[#F8F9FA]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Person</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#4B5563]">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-[#4B5563]">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Date Paid</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Approved By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Notes</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#4B5563]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reimbursements.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#9CA3AF]">No reimbursements found.</td></tr>
              ) : reimbursements.map((r) => (
                <tr key={r.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFAFA]">
                  <td className="px-4 py-3 text-sm font-medium">{r.person_name}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium">${r.total_amount.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-sm">{r.date_paid || '-'}</td>
                  <td className="px-4 py-3 text-sm">{r.approved_by || '-'}</td>
                  <td className="px-4 py-3 text-sm max-w-xs truncate">{r.payment_notes || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEditReimb(r)} className="p-1 text-[#4B5563] hover:text-[#BA1826]"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteReimb(r.id)} className="p-1 text-[#4B5563] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
